var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Timeouts } from './framework/Timeouts';
const applicationMediaOperationCaps = [
    'back',
    'closedCaptioningToggle',
    'directionEnter',
    'directionUpDownLeftRight',
    'exit',
    'keyDash',
    'keyDigits',
    'keyEnter',
    'mediaPlayerForward',
    'mediaPlayerPause',
    'mediaPlayerPlay',
    'mediaPlayerReverse',
    'mediaPlayerStop',
    'menu',
];
const remoteKeys = {
    back: 'return',
    channelDown: 'channelDn',
    channelUp: 'channelUp',
    closedCaptioningToggle: 'caption',
    directionDown: 'cursorDn',
    directionEnter: 'enter',
    directionLeft: 'cursorLeft',
    directionRight: 'cursorRight',
    directionUp: 'cursorUp',
    exit: 'exit',
    key0: 'number0',
    key1: 'number1',
    key2: 'number2',
    key3: 'number3',
    key4: 'number4',
    key5: 'number5',
    key6: 'number6',
    key7: 'number7',
    key8: 'number8',
    key9: 'number9',
    keyDash: 'dash',
    keyEnter: 'enter',
    mediaPlayerForward: 'fastforward',
    mediaPlayerPause: 'pause',
    mediaPlayerPlay: 'play',
    mediaPlayerReverse: 'rewind',
    mediaPlayerStop: 'stop',
    menu: 'menu',
};
const modelInfo = {
    '-MVISh-LSD6yUCLMpCi2': {
        name: 'BET-H Series',
        port: 1516,
    },
    '-MVISlFqC07PuVkCY4Yq': {
        name: 'QN Series',
        port: 1515,
    },
};
const videoInputCodes = [
    { code: 'amazon', type: 'application' },
    { code: 'hulu', type: 'application' },
    { code: 'netflix', type: 'application' },
    { code: 'pandora', type: 'application' },
    { code: 'vudu', type: 'application' },
    { code: 'webBrowser', type: 'application' },
    { code: 'youTube', type: 'application' },
    { code: 'AV1', type: 'inputSource' },
    { code: 'COMPONENT1', type: 'inputSource' },
    { code: 'HDMI1', type: 'inputSource' },
    { code: 'HDMI2', type: 'inputSource' },
    { code: 'HDMI3', type: 'inputSource' },
    { code: 'HDMI4', type: 'inputSource' },
    { code: ['TV', 'dtv', 'atv'], type: 'inputSource' },
    { code: 'USB', type: 'inputSource' },
];
function convertToCode(value) {
    value = value.toLowerCase();
    return videoInputCodes.find(code => {
        if (Array.isArray(code.code)) {
            return code.code.find(code => value === code.toLowerCase());
        }
        else {
            return value === code.code.toLowerCase();
        }
    });
}
function isErrorCode(resp, code) {
    return resp.error !== undefined && typeof resp.error === 'object' && resp.error.code === code;
}
function isSslError(resp) {
    return (resp.status === -1 &&
        resp.error !== undefined &&
        typeof resp.error === 'string' &&
        resp.error === 'SSL handshake failed');
}
export class SamsungProTv {
    constructor(http, state, log, framework) {
        this.http = http;
        this.state = state;
        this.log = log;
        this.framework = framework;
        this.isAsleep = true;
        this.port = 1515;
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.channel = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                const resp = (yield this.request({ method: 'directChannelControl' }, { tags }));
                if (resp.error || ((_a = resp.result) === null || _a === void 0 ? void 0 : _a.channelNum) === undefined) {
                    return this.state.channel.value;
                }
                return { channel: resp.result.channelNum };
            }),
        };
        this.channelKeyboardType = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () { return 'numericWithDash'; }),
        };
        this.mediaOperation = {
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const remoteKey = remoteKeys[value.operation];
                if (remoteKey === undefined) {
                    throw new Error(`no remote key for operation ${value.operation}`);
                }
                if (key !== undefined) {
                    yield this.request({ method: 'remoteKeyControl', params: { remoteKey } }, { tags });
                }
                else if (value.operation === 'channelSet') {
                    // TODO: this doesn't work yet, figure it out
                    yield this.request({
                        method: 'directChannelControl',
                        params: { channelNum: value.channel },
                    });
                }
                else if (value.operation === 'channelUp' || value.operation === 'channelDown') {
                    yield this.request({
                        method: 'channelUpDnControl',
                        params: {
                            control: value.operation === 'channelUp' ? 'channelUp' : 'channelDn',
                        },
                    });
                }
                else {
                    this.log.error(`unhandled media request: ${JSON.stringify(value)}`);
                }
                return value;
            }),
        };
        this.mediaOperations = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _b, _c;
                const mediaOperationCaps = {};
                for (const input of Object.values((_c = (_b = this.config) === null || _b === void 0 ? void 0 : _b.videoInputs) !== null && _c !== void 0 ? _c : {})) {
                    const code = convertToCode(input.code);
                    if (code === undefined) {
                        this.log.error(`unrecognized video input: ${JSON.stringify(input)}`);
                        continue;
                    }
                    if (code.type === 'application') {
                        mediaOperationCaps[input.code] = [...applicationMediaOperationCaps];
                    }
                    else if (Array.isArray(code.code) && code.code[0] === 'TV') {
                        mediaOperationCaps[input.code] = [
                            ...applicationMediaOperationCaps,
                            'channelSet',
                            'channelUpDown',
                        ];
                    }
                }
                return mediaOperationCaps;
            }),
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                // If the power query times out, assume we're powered off.
                var _d;
                try {
                    const resp = (yield this.request({ method: 'powerControl' }, { tags, timeoutMs: 1000 }));
                    if (resp.error) {
                        this.handleError(resp);
                        return this.state.power.value;
                    }
                    if (tags.includes('poll') && this.framework.isPollingSuspended) {
                        return this.state.power.value;
                    }
                    this.isAsleep = false;
                    return ((_d = resp.result) === null || _d === void 0 ? void 0 : _d.power) === 'powerOn';
                }
                catch (error) {
                    if (error.code === 'sendTimeout') {
                        if (tags.includes('poll') && this.framework.isPollingSuspended) {
                            return this.state.power.value;
                        }
                        this.isAsleep = true;
                        return false;
                    }
                    throw error;
                }
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                var _e, _f;
                const delayMs = 7000;
                this.busyTimeout.clear();
                // The power state can be incorrectly reported for a short time after setting the power,
                // so we suspend polling to give it time to settle out.
                this.framework.suspendPolling(delayMs);
                if (power) {
                    // Sending 5 packets might not have been enough at Yale (SNTY-2949).
                    yield this.framework.sendWakeOnLan({ count: 10 });
                    if (this.isAsleep) {
                        this.state.power.update(true);
                        this.state.busy.update(true);
                        yield this.timeouts.delay(delayMs);
                        const startTime = Date.now();
                        let newPower;
                        // We have seen that some devices become unresponsive for a while after
                        // powering on, so we querying power until we get a response. This stalls the
                        // framework and prevents any other commands from going out until we know the
                        // device is responsive.
                        while (newPower === undefined && startTime + 75000 > Date.now()) {
                            try {
                                const resp = (yield this.request({ method: 'powerControl' }, { tags, timeoutMs: 2000 }));
                                if (resp.error === undefined) {
                                    newPower = ((_e = resp.result) === null || _e === void 0 ? void 0 : _e.power) === 'powerOn';
                                }
                                else {
                                    yield this.timeouts.delay(2000);
                                }
                            }
                            catch (error) {
                                // ignore errors and keep trying
                                if (error.code !== 'sendTimeout') {
                                    yield this.timeouts.delay(2000);
                                }
                            }
                        }
                        if (newPower === undefined) {
                            this.log.warning(`timeout waiting for power`);
                            newPower = false;
                        }
                        this.state.busy.update(false);
                        return this.state.power.value;
                    }
                }
                const resp = (yield this.request({ method: 'powerControl', params: { power: power ? 'powerOn' : 'powerOff' } }, { tags }));
                if (resp.error || ((_f = resp.result) === null || _f === void 0 ? void 0 : _f.power) === undefined) {
                    if (this.handleError(resp))
                        this.framework.resume();
                    return this.state.power.value;
                }
                this.state.power.update(resp.result.power === 'powerOn');
                this.state.busy.update(true);
                yield this.timeouts.delay(delayMs);
                this.state.busy.update(false);
                return this.state.power.value;
            }),
        };
        this.videoInput = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _g, _h;
                const aresp = (yield this.request({ method: 'directAccessControl' }, { tags }));
                if (((_g = aresp.result) === null || _g === void 0 ? void 0 : _g.applicationName) !== undefined &&
                    aresp.result.applicationName !== 'unknown') {
                    const code = convertToCode(aresp.result.applicationName);
                    if (code === undefined) {
                        this.log.error(`unrecognized applicationName: ${aresp.result.applicationName}`);
                        return aresp.result.applicationName;
                    }
                    return Array.isArray(code.code) ? code.code[0] : code.code;
                }
                const iresp = (yield this.request({ method: 'inputSourceControl' }, { tags }));
                if (iresp.error || ((_h = iresp.result) === null || _h === void 0 ? void 0 : _h.inputSource) === undefined) {
                    this.handleError(iresp);
                    return this.state.videoInput.value;
                }
                const code = convertToCode(iresp.result.inputSource);
                if (code === undefined) {
                    this.log.error(`unrecognized inputSource: ${iresp.result.inputSource}`);
                    return iresp.result.inputSource;
                }
                return Array.isArray(code.code) ? code.code[0] : code.code;
            }),
            set: (value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _j, _k;
                let code = convertToCode(value);
                if (code === undefined) {
                    this.log.error(`unrecognized videoInput code: ${value}`);
                    code = { code: value, type: 'inputSource' };
                }
                const paramCode = Array.isArray(code.code) ? code.code[0] : code.code;
                if (code.type === 'inputSource') {
                    const resp = (yield this.request({ method: 'inputSourceControl', params: { inputSource: paramCode } }, { tags }));
                    if (resp.error || ((_j = resp.result) === null || _j === void 0 ? void 0 : _j.inputSource) === undefined) {
                        this.handleError(resp);
                        return this.state.videoInput.value;
                    }
                    value = resp.result.inputSource;
                }
                else {
                    const resp = (yield this.request({ method: 'directAccessControl', params: { applicationName: paramCode } }, { tags }));
                    if (resp.error || ((_k = resp.result) === null || _k === void 0 ? void 0 : _k.applicationName) === undefined) {
                        this.handleError(resp);
                        return this.state.videoInput.value;
                    }
                    value = resp.result.applicationName;
                }
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), code.type === 'application' ? 7000 : 2000);
                return value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.volume = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _l;
                const resp = (yield this.request({ method: 'directVolumeControl' }, { tags }));
                if (resp.error || ((_l = resp.result) === null || _l === void 0 ? void 0 : _l.volume) === undefined) {
                    this.handleError(resp);
                    return this.state.volume.value;
                }
                const volume = parseInt(resp.result.volume, 10);
                if (Number.isNaN(volume)) {
                    this.log.error(`volume.get: error: ${JSON.stringify(resp, null, 4)}`);
                    return this.state.volume.value;
                }
                return volume;
            }),
            set: (volume, tags) => __awaiter(this, void 0, void 0, function* () {
                var _m;
                const resp = (yield this.request({ method: 'directVolumeControl', params: { volume: volume.toString() } }, { tags }));
                if (resp.error || ((_m = resp.result) === null || _m === void 0 ? void 0 : _m.volume) === undefined) {
                    this.handleError(resp);
                    return this.state.volume.value;
                }
                volume = parseInt(resp.result.volume, 10);
                if (Number.isNaN(volume)) {
                    this.log.error(`volume.set: error: ${JSON.stringify(resp, null, 4)}`);
                    return this.state.volume.value;
                }
                return volume;
            }),
            powerOffRestrictions: ['get', 'set'],
            range: { max: 100, min: 0, round: 1 },
        };
        this.volumeMute = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _o;
                const resp = (yield this.request({ method: 'muteControl' }, { tags }));
                if (resp.error || ((_o = resp.result) === null || _o === void 0 ? void 0 : _o.mute) === undefined) {
                    this.handleError(resp);
                    return this.state.volumeMute.value;
                }
                return resp.result.mute === 'muteOn';
            }),
            set: (mute, tags) => __awaiter(this, void 0, void 0, function* () {
                var _p;
                const resp = (yield this.request({ method: 'muteControl', params: { mute: mute ? 'muteOn' : 'muteOff' } }, { tags }));
                if (resp.error || ((_p = resp.result) === null || _p === void 0 ? void 0 : _p.mute) === undefined) {
                    this.handleError(resp);
                    return this.state.volumeMute.value;
                }
                return resp.result.mute === 'muteOn';
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        // polling
        this.polls = [
            { intervalMs: 3000, controls: ['power', 'videoInput', 'volume', 'volumeMute'] },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.config = config;
            if (this.accessToken === undefined) {
                this.log.error(`onConfigure: accessToken undefined`);
            }
            else {
                this.state.storage.update({ accessToken: this.accessToken });
            }
            this.timeouts.clearAll();
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials, config) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            this.port = (_d = (_a = config.port) !== null && _a !== void 0 ? _a : (_c = modelInfo[(_b = config.modelNumber) !== null && _b !== void 0 ? _b : '']) === null || _c === void 0 ? void 0 : _c.port) !== null && _d !== void 0 ? _d : 1515;
            if (credentials.doInteractiveAuthentication) {
                let result = yield this.requestAccessToken();
                if (result.type === 'error') {
                    if (result.code === 'error') {
                        return 'authFailed';
                    }
                    else if (result.code === 'timeout') {
                        return 'connectFailed';
                    }
                    else {
                        return 'verifyFailed';
                    }
                }
                result = yield this.confirmAccessToken(result.accessToken);
                if (result.type === 'error')
                    return 'authFailed';
                this.accessToken = result.accessToken;
                this.state.power.update(true);
                return 'connected';
            }
            else {
                if (this.state.storage.value.accessToken === undefined)
                    return 'authFailed';
                const result = yield this.confirmAccessToken(this.state.storage.value.accessToken);
                if (result.type === 'error') {
                    if (result.code === 'error') {
                        return 'authFailed';
                    }
                    else if (result.code === 'cantSend' || result.code === 'timeout') {
                        // If we have an access token, but can't get a response, then assume the device
                        // is powered off.
                        this.accessToken = this.state.storage.value.accessToken;
                        return 'connected';
                    }
                    else if (result.code === 'sslError') {
                        return 'connectFailed';
                    }
                    else {
                        return 'verifyFailed';
                    }
                }
                this.accessToken = result.accessToken;
                this.state.power.update(true);
                return 'connected';
            }
        });
    }
    // other class methods
    confirmAccessToken(accessToken) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const resp = (yield this.request({ method: 'powerControl' }, { accessToken }));
                if (((_a = resp.result) === null || _a === void 0 ? void 0 : _a.power) !== undefined) {
                    return { type: 'accessToken', accessToken };
                }
                else {
                    return { type: 'error', code: 'error' };
                }
            }
            catch (error) {
                if (error.code === 'sendTimeout') {
                    this.isAsleep = true;
                    return { type: 'error', code: 'timeout' };
                }
                else if (error.code === 'cantSend') {
                    this.isAsleep = true;
                    return { type: 'error', code: 'cantSend' };
                }
                return { type: 'error', code: 'unknown' };
            }
        });
    }
    handleError(resp) {
        if (isSslError(resp)) {
            this.framework.changePhase('disconnected');
            return false;
        }
        else if (isErrorCode(resp, -32002)) {
            // Some models return this error if we select a video input with no source attached.
            return true;
        }
        else {
            this.log.error(`error: ${JSON.stringify(resp, null, 4)}`);
            return true;
        }
    }
    requestAccessToken() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const resp = (yield this.request({ method: 'createAccessToken' }, { timeoutMs: 30000 }));
                if (((_a = resp.result) === null || _a === void 0 ? void 0 : _a.AccessToken) !== undefined) {
                    return { type: 'accessToken', accessToken: resp.result.AccessToken };
                }
                else {
                    return { type: 'error', code: 'error' };
                }
            }
            catch (error) {
                if (error.code === 'sendTimeout') {
                    this.isAsleep = true;
                    return { type: 'error', code: 'timeout' };
                }
                else if (error.code === 'cantSend') {
                    this.isAsleep = true;
                    return { type: 'error', code: 'cantSend' };
                }
                return { type: 'error', code: 'unknown' };
            }
        });
    }
    request(data, options) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (SamsungProTv.requestId >= Number.MAX_SAFE_INTEGER) {
                SamsungProTv.requestId = 1;
            }
            else {
                SamsungProTv.requestId++;
            }
            const requestOptions = {};
            requestOptions.data = { id: SamsungProTv.requestId, jsonrpc: '2.0', method: data.method };
            requestOptions.headers = { Accept: 'application/json' };
            if (options === null || options === void 0 ? void 0 : options.tags)
                requestOptions.tags = options.tags;
            if ((options === null || options === void 0 ? void 0 : options.timeoutMs) !== undefined)
                requestOptions.timeoutMs = options.timeoutMs;
            if (data.method !== 'createAccessToken') {
                requestOptions.data.params = Object.assign(Object.assign({}, ((_a = data.params) !== null && _a !== void 0 ? _a : {})), { AccessToken: (_b = options === null || options === void 0 ? void 0 : options.accessToken) !== null && _b !== void 0 ? _b : this.accessToken });
            }
            try {
                const resp = this.http.requestJson('post', `:${this.port}`, requestOptions);
                if (this.framework.phase === 'controlling' && isSslError(resp)) {
                    this.timeouts.set(() => this.framework.changePhase('disconnected'), 100);
                }
                return resp;
            }
            catch (error) {
                if ((error.code === 'cantSend' || error.code === 'sendTimeout') &&
                    this.framework.phase === 'controlling') {
                    this.log.info(`command failed: ${error.code}, assuming device is asleep`);
                    this.isAsleep = true;
                    this.state.power.update(false);
                    // TODO: It seems odd that we're not returning undefined or some error code here.
                    // Instead, we fall through and throw the error, which doesn't seem like the intent
                    // of the code. If we fix this to not throw the cantSend and sendTimeout errors,
                    // then we will also need to adjust the try/catch loop in power.set -- it depends
                    // on receiving those errors.
                }
                throw error;
            }
        });
    }
}
// static properties
SamsungProTv.driverType = 'httpDriver';
SamsungProTv.key = '-MVIScyKrq9G-ARWbrSv';
SamsungProTv.requestId = 0;
SamsungProTv.httpRequestOptions = {
    prettyCommands: true,
    scheme: 'https',
    idleTimeoutMs: 0,
};
